/**
 * Utility functions to transform data between different formats
 * Primarily used to convert snake_case (from database) to camelCase (for frontend)
 */

/**
 * Converts a snake_case or PascalCase_with_underscores string to camelCase
 * @param {string} str - The snake_case string
 * @returns {string} - The camelCase string
 */
const snakeToCamel = (str) => {
  // First convert to lowercase, then apply camelCase
  return str.toLowerCase().replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
};

/**
 * Converts an object's keys from snake_case to camelCase
 * @param {Object} obj - The object with snake_case keys
 * @returns {Object} - The object with camelCase keys
 */
const objectKeysToCamel = (obj) => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return obj;
  }
  
  const camelObj = {};
  Object.keys(obj).forEach(key => {
    const camelKey = snakeToCamel(key);
    const value = obj[key];
    
    // Recursively convert nested objects
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      camelObj[camelKey] = objectKeysToCamel(value);
    } else if (Array.isArray(value)) {
      camelObj[camelKey] = value.map(item => 
        typeof item === 'object' ? objectKeysToCamel(item) : item
      );
    } else {
      camelObj[camelKey] = value;
    }
  });
  
  return camelObj;
};

/**
 * Normalizes an array of farmer objects from snake_case to camelCase
 * @param {Array} farmers - Array of farmer objects with snake_case keys
 * @returns {Array} - Array of farmer objects with camelCase keys
 */
export const normalizeFarmers = (farmers) => {
  if (!Array.isArray(farmers)) {
    return [];
  }
  
  return farmers.map(farmer => {
    const camelFarmer = objectKeysToCamel(farmer);
    
    // Map database-specific fields to frontend fields based on actual database schema
    // If the DB is normalized, the name may be in 'name' (e.g., 'DE LA VEGA, FEDERICO')
    let fullName = '';
    let displayName = '';
    if (camelFarmer.firstname || camelFarmer.lastname) {
      fullName = [camelFarmer.firstname, camelFarmer.middleInitial, camelFarmer.lastname]
        .filter(Boolean)
        .join(' ')
        .trim();
      displayName = [camelFarmer.firstname, camelFarmer.lastname]
        .filter(Boolean)
        .join(' ')
        .trim();
    } else if (camelFarmer.name) {
      // Use the 'name' field directly from the DB if present
      fullName = camelFarmer.name;
      displayName = camelFarmer.name;
    }
    return {
      // Keep all original fields
      ...camelFarmer,
      // Core identification
      id: camelFarmer.profileId || camelFarmer.profile_id,
      name: fullName || '',
      displayName: displayName || '',
      
      // Main Farmers Table fields
      lastname: camelFarmer.lastname || '',
      firstname: camelFarmer.firstname || '',
      middleInitial: camelFarmer.middleInitial || '',
      respondent: camelFarmer.respondent || '',
      sitioPurok: camelFarmer.sitioPurok || '',
      barangay: camelFarmer.barangay || '',
      municipality: camelFarmer.municipality || '',
      
      // Build address from location fields
      address: [camelFarmer.sitioPurok, camelFarmer.barangay, camelFarmer.municipality]
        .filter(Boolean)
        .join(', ') || '',
      
      age: camelFarmer.age || 0,
      gender: camelFarmer.gender ? String(camelFarmer.gender).trim() : '',
      civilStatus: camelFarmer.civilStatus || '',
      educationalAttainment: camelFarmer.educationalAttainment || '',
      noOfHouseholdMembers: camelFarmer.noOfHouseholdMembers || 0,
      occupation: camelFarmer.occupation || '',
      
      // Farmer_Income Table fields
      annualFarmIncomeFromCacao: camelFarmer.annualFarmIncomeFromCacao || '',
      formOfIncome: camelFarmer.formOfIncome || '',
      inPesoIncome: camelFarmer.inPesoIncome || 0,
      inKindIncome: camelFarmer.inKindIncome || '',
      
      // Farmer_Membership Table fields
      affiliationNameOfOrganizationCooperative: camelFarmer.affiliationNameOfOrganizationCooperative || '',
      yearsInMembership: camelFarmer.yearsInMembership || 0,
      active: camelFarmer.active || false,
      position: camelFarmer.position || '',
      designation: camelFarmer.designation || '',
      
      // Farmer_Farm Table fields
      farmAreaHa: camelFarmer.farmAreaHa || 0,
      farmSize: camelFarmer.farmAreaHa || 0,  // Add farmSize alias for UI
      yearEstablished: camelFarmer.yearEstablished || null,
      actualNumberOfTreesPlanted: camelFarmer.actualNumberOfTreesPlanted || 0,
      noOfProductiveTreesBeforeTyphoon: camelFarmer.noOfProductiveTreesBeforeTyphoon || 0,
      noOfProductiveTreesAfterTyphoon: camelFarmer.noOfProductiveTreesAfterTyphoon || 0,
      sourceProvider: camelFarmer.sourceProvider || '',
      propagationTechnique: camelFarmer.propagationTechnique || '',
      landOwnership: camelFarmer.landOwnership || '',
      varietyPlanted: camelFarmer.varietyPlanted || '',
      clones: camelFarmer.clones || '',
      availabilityOfNursery: camelFarmer.availabilityOfNursery || '',
      
      // Parse varieties from varietyPlanted string to array for UI
      cacaoVarieties: camelFarmer.varietyPlanted 
        ? (typeof camelFarmer.varietyPlanted === 'string' 
            ? camelFarmer.varietyPlanted.split(',').map(v => v.trim()).filter(Boolean)
            : [camelFarmer.varietyPlanted])
        : [],
      
      // Farmer_Productivity Table fields
      productivityPodsTreeYear: camelFarmer.productivityPodsTreeYear || 0,
      podsPerTreeHarvest: camelFarmer.podsPerTreeHarvest || 0,
      weightOfBeansPerHarvestKg: camelFarmer.weightOfBeansPerHarvestKg || 0,
      weightOfBeansPerHectareKg: camelFarmer.weightOfBeansPerHectareKg || 0,
      
      // Farmer_Knowledge Table fields
      knowledgeOnCacaoFarmingTrainingsSeminarAttended: camelFarmer.knowledgeOnCacaoFarmingTrainingsSeminarAttended || '',
      production: camelFarmer.production || ''
    };
  });
};

/**
 * Normalizes an array of processor objects from snake_case to camelCase
 * @param {Array} processors - Array of processor objects with snake_case keys
 * @returns {Array} - Array of processor objects with camelCase keys
 */
export const normalizeProcessors = (processors) => {
  if (!Array.isArray(processors)) {
    return [];
  }
  
  return processors.map(processor => {
    const camelProcessor = objectKeysToCamel(processor);
    
    // Map database-specific fields to frontend fields based on your SP structure
    return {
      // Keep all original fields
      ...camelProcessor,
      
      // Core identification - from normalized table
      id: camelProcessor.profileId || camelProcessor.profile_id,
      businessName: camelProcessor.nameOfBusiness || camelProcessor.businessName || camelProcessor.name || '',
      displayName: camelProcessor.nameOfBusiness || camelProcessor.displayName || camelProcessor.businessName || camelProcessor.name || '',
      
      // Business table fields
      address: camelProcessor.address || '',
      municipality: camelProcessor.municipality || '',
      barangay: camelProcessor.barangay || '',
      contactNumbers: camelProcessor.contactNumberS ? 
        (typeof camelProcessor.contactNumberS === 'string' ? 
          camelProcessor.contactNumberS.split(',').map(c => c.trim()) : 
          [camelProcessor.contactNumberS]) : 
        [],
      contact: camelProcessor.contactNumberS || camelProcessor.contact || '',
      lengthOfBusinessOperationInYears: camelProcessor.lengthOfBusinessOperationInYears || 0,
      yearsInOperation: camelProcessor.lengthOfBusinessOperationInYears || camelProcessor.yearsInOperation || 0,
      statusOfBusiness: camelProcessor.statusOfBusiness || camelProcessor.businessStatus || 'Active',
      businessStatus: camelProcessor.statusOfBusiness || camelProcessor.businessStatus || 'Active',
      typeOfBusiness: camelProcessor.typeOfBusiness || camelProcessor.businessType || '',
      businessType: camelProcessor.typeOfBusiness || camelProcessor.businessType || '',
      nameOfTheOwner: camelProcessor.nameOfTheOwner || camelProcessor.ownerName || '',
      ownerName: camelProcessor.nameOfTheOwner || camelProcessor.ownerName || '',
      addressOwner: camelProcessor.addressOwner || '',
      initialInvestment: camelProcessor.initialInvestment || 0,
      averageAnnualGrossIncome: camelProcessor.averageAnnualGrossIncomeLast5YearsInPesos || camelProcessor.averageAnnualGrossIncome || 0,
      numberOfEmployees: camelProcessor.numberOfEmployees || 0,
      employeeType: camelProcessor.type || camelProcessor.employeeType || '',
      latitude: camelProcessor.latitude || null,
      longitude: camelProcessor.longitude || null,
      
      // Processing table fields (prefixed with 4_)
      quantityOfCacaoBeansPerProcessingCycle: camelProcessor.quantityOfCacaoBeansPerProcessingCycleKg || 0,
      processingCapacity: camelProcessor.quantityOfCacaoBeansPerProcessingCycleKg || camelProcessor.processingCapacity || 0,
      frequencyOfProcessing: camelProcessor.frequencyOfProcessing || '',
      preferredVarietyOfCacao: camelProcessor['41PreferredVarietyOfCacao'] || '',
      varietyOfCacaoProcessed: camelProcessor['42VarietyOfCacaoProcessed'] || '',
      sourceOfCacao: camelProcessor['43SourceOfCacao'] || '',
      producersToProcessors: camelProcessor['441ProducersToProcessors'] || '',
      localTradersToProcessors: camelProcessor['442LocalTradersToProcessors'] || '',
      frequencyOfDelivery: camelProcessor['45FrequencyOfDeliveryInAMonth'] || '',
      quantityOfCacaoDelivered: camelProcessor['46QuantityKilogramsOfCacaoDelivered'] || 0,
      formTypeOfCacaoBought: camelProcessor['47FormTypeOfCacaoBought'] || '',
      formType: camelProcessor.formType || '',
      
      // Post Harvest table fields
      podBreaking: camelProcessor['48PodBreaking'] || '',
      beanExtraction: camelProcessor['49BeanExtraction'] || '',
      fermentation: camelProcessor['410Fermentation'] || '',
      containmentMaterials: camelProcessor.containmentMaterials || '',
      numberOfDays: camelProcessor.numberOfDays || '',
      temperatureMonitoring: camelProcessor.temperatureMonitoring || '',
      preferredFermentationTemperature: camelProcessor.preferredFermentationTemperature || '',
      dryingReDrying: camelProcessor['411DryingReDrying'] || '',
      dryingType: camelProcessor.typeDrying || camelProcessor.dryingType || '',
      preferredMoistureContent: camelProcessor['4111PreferredMoistureContent'] || '',
      methodOfMcDetermination: camelProcessor.methodOfMcDetermination || '',
      beanSorting: camelProcessor['412BeanSorting'] || '',
      usualBeanSize: camelProcessor.usualBeanSize || '',
      beanGrading: camelProcessor['413BeanGrading'] || '',
      levelOfFermentation: camelProcessor.levelOfFermentationPercent || '',
      rawBeanAroma: camelProcessor.rawBeanAroma || '',
      materialsUsedForDriedBeanStorage: camelProcessor['414MaterialsUsedForDriedBeanStorage'] || '',
      estimatedCostOfMaterials: camelProcessor.estimatedCostOfMaterials || 0,
      
      // Processing Techniques table fields
      roasting: camelProcessor['415Roasting'] || '',
      roastingMethod: camelProcessor.roastingMethod || '',
      averageTimeSpent: camelProcessor.averageTimeSpent || '',
      roastingTemperatureMonitoring: camelProcessor.roastingTemperatureMonitoring || '',
      preferredRoastingTemperature: camelProcessor.preferredRoastingTemperature || '',
      cracking: camelProcessor['416Cracking'] || '',
      crackingType: camelProcessor.crackingType || '',
      winnowing: camelProcessor['417Winnowing'] || '',
      winnowingType: camelProcessor.winnowingType || '',
      grinding: camelProcessor['418Grinding'] || '',
      grindingType: camelProcessor.grindingType || '',
      conching: camelProcessor['419Conching'] || '',
      preferredMicronsSize: camelProcessor.preferredMicronsSize || '',
      tempering: camelProcessor['420Tempering'] || '',
      temperingType: camelProcessor.temperingType || '',
      temperingTemp: camelProcessor.preferredTemperingTemperature || '',
      moulding: camelProcessor['421Moulding'] || '',
      mouldingType: camelProcessor.mouldingType || '',
      storageOfProcessedProduct: camelProcessor['422StorageOfProcessedProduct'] || '',
      preferredStorageTemperature: camelProcessor.preferredStorageTemperature || '',
      preferredRelativeHumidity: camelProcessor.preferredRelativeHumidityRh || '',
      packagingMaterial: camelProcessor['423PackagingMaterial'] || '',
      
      // Marketing table fields
      marketing: camelProcessor['5Marketing'] || '',
      typeOfProductSold: camelProcessor['51TypeOfProductSold'] ? 
        (typeof camelProcessor['51TypeOfProductSold'] === 'string' ? 
          camelProcessor['51TypeOfProductSold'].split(',').map(p => p.trim()) : 
          [camelProcessor['51TypeOfProductSold']]) : 
        [],
      typeOfMarketing: camelProcessor['52TypeOfMarketing'] || '',
      market: camelProcessor['53Market'] || '',
      marketReach: camelProcessor.marketReach || camelProcessor['53Market'] || 'Local',
      marketplace: camelProcessor.marketplace || '',
      localMarket: camelProcessor.localMarket || false,
      international: camelProcessor.international || false,
      
      // Business Insights table fields
      whatInterestYouToGoOnCacaoProcessing: camelProcessor.whatInterestYouOnCacaoProcessingTableyaChocolate || '',
      challengesEncounteredInProcessing: camelProcessor.challengesEncounteredInProcessingTableyaChocolate || '',
      supportsNeededToImprove: camelProcessor.whatSupportsYouNeedToImproveBusiness || '',
      futurePlans: camelProcessor.whatAreYourFuturePlans || ''
    };
  });
};

/**
 * Converts a camelCase string to snake_case
 * @param {string} str - The camelCase string
 * @returns {string} - The snake_case string
 */
const camelToSnake = (str) => {
  return str.replace(/([A-Z])/g, (match) => `_${match.toLowerCase()}`);
};

/**
 * Converts an object's keys from camelCase to snake_case
 * @param {Object} obj - The object with camelCase keys
 * @returns {Object} - The object with snake_case keys
 */
export const objectKeysToSnake = (obj) => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return obj;
  }
  
  const snakeObj = {};
  Object.keys(obj).forEach(key => {
    const snakeKey = camelToSnake(key);
    const value = obj[key];
    
    // Recursively convert nested objects
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      snakeObj[snakeKey] = objectKeysToSnake(value);
    } else if (Array.isArray(value)) {
      snakeObj[snakeKey] = value.map(item => 
        typeof item === 'object' ? objectKeysToSnake(item) : item
      );
    } else {
      snakeObj[snakeKey] = value;
    }
  });
  
  return snakeObj;
};
